/**
 * @breathly/web — Auth context.
 *
 * Manages Supabase Auth session state and provides sign-in/sign-out to the
 * component tree. When the user signs in, the API client's token is updated
 * so subsequent API calls include the Bearer JWT.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { type User } from '@supabase/supabase-js';
import { getSupabase, getSupabaseConfigError } from './supabase.js';
import { api } from './api.js';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export interface AuthState {
  /** The user, or null if not authenticated. */
  readonly user: User | null;
  /** True while the initial session is being loaded. */
  readonly loading: boolean;
  /** Non-null when auth cannot initialize. */
  readonly error: string | null;
  /** Sign in with email and password. */
  readonly signIn: (email: string, password: string) => Promise<void>;
  /** Sign out the current user. */
  readonly signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface AuthProviderProps {
  readonly children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(() => getSupabaseConfigError());

  // -- Session restoration on mount ---------------------------------------
  useEffect(() => {
    if (error) {
      setLoading(false);
      return;
    }

    let supabase;
    try {
      supabase = getSupabase();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to initialize authentication.');
      setLoading(false);
      return;
    }

    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        if (session?.access_token) {
          api.setToken(session.access_token);
          setUser(session.user ?? null);
        }
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Unable to restore authentication session.');
      })
      .finally(() => {
        setLoading(false);
      });

    // Listen for auth state changes (sign in, sign out, token refresh)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.access_token) {
        api.setToken(session.access_token);
        setUser(session.user ?? null);
      } else {
        api.clearToken();
        setUser(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [error]);

  // -- Sign in ------------------------------------------------------------
  const signIn = useCallback(async (email: string, password: string) => {
    if (error) throw new Error(error);
    const supabase = getSupabase();
    const { data, error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (signInError) {
      throw signInError;
    }
    if (data.session?.access_token) {
      api.setToken(data.session.access_token);
      setUser(data.session.user ?? null);
    }
  }, [error]);

  // -- Sign out -----------------------------------------------------------
  const signOut = useCallback(async () => {
    if (error) throw new Error(error);
    const supabase = getSupabase();
    await supabase.auth.signOut();
    // onAuthStateChange will handle clearing the token and user
  }, [error]);

  return (
    <AuthContext.Provider value={{ user, loading, error, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
